import React, { useCallback, useEffect, useRef, useState } from "react";
import { BarMilestone, BarTask } from "../../types/bar-task";
import {
  GanttContentMoveAction,
  GanttEvent,
} from "../../types/gantt-task-actions";
import { Bar } from "./bar/bar";
import { Milestone } from "./milestone/milestone";
import { Project } from "./project/project";
import style from "./task-list.module.css";
import { Task } from "../../types/public-types";
import UserAvatar from "@/components/ui/userAvatar";
import { Link, useLocation } from "react-router-dom";
import { calculateTaskEffort } from "@/hooks/useEffortCalculation";

export type TaskItemProps = {
  startDrag(e: any, data: any): void;
  endDrag(e: any, data: any): void;
  canCreateDependency: boolean;
  task: BarTask;
  arrowIndent: number;
  taskHeight: number;
  isProgressChangeable: boolean;
  isDateChangeable: boolean;
  isDelete: boolean;
  isSelected: boolean;
  rtl: boolean;
  selectedTasks: Task | BarTask | any;
  setGanttEvent: (value: GanttEvent) => void;
  onselectionchange: () => void;
  onEventStart: (
    action: GanttContentMoveAction,
    selectedTask: BarTask,
    event?: React.MouseEvent | React.KeyboardEvent,
  ) => any;
  pop: string[];
  allow: boolean;
  ganttEvent: GanttEvent;
  /** Passed by TaskItem to Bar/Project for roadmap milestone diamonds */
  onMilestoneMouseEnter?: (e: React.MouseEvent, milestone: BarMilestone) => void;
  onMilestoneMouseLeave?: (e: React.MouseEvent) => void;
};

export const TaskItem: React.FC<TaskItemProps> = (props) => {
  const {
    task,
    arrowIndent,
    isDelete,
    isSelected,
    rtl,
    selectedTasks,
    onEventStart,
    onselectionchange,
    pop,
    allow,
    setGanttEvent,
    ganttEvent,
    canCreateDependency = true,
  } = {
    ...props,
  };
  let taskHeight = props.taskHeight;
  const textRef = useRef<SVGTextElement>(null);
  const [taskItem, setTaskItem] = useState<JSX.Element>(<div />);
  const [isTextInside, setIsTextInside] = useState(false);

  // State for tooltip
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });
  const [userWidth, setUserWidth] = useState(36);
  const [baselineItem, setBaselineItem] = useState<JSX.Element | null>(null);

  const handleMilestoneMouseEnter = useCallback(
    (e: React.MouseEvent, milestone: BarMilestone) => {
      e.stopPropagation();
      onEventStart(
        "mouseenter",
        {
          ...task,
          name: milestone.name,
          start: milestone.start,
          end: new Date(milestone.start.getTime() + 86400000),
          duration: 1,
          progress: task.progress ?? 0,
          ganttRoadmapMilestone: { parentBarLabel: task.name },
        } as BarTask,
        e,
      );
    },
    [task, onEventStart],
  );

  const handleMilestoneMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEventStart("mouseenter", task, e);
    },
    [task, onEventStart],
  );

  useEffect(() => {
    const baselineCount = task.baseLineTask?.length || 0;
    const hasBaseline = baselineCount > 0;

    // Dynamically reduce height based on number of baselines
    const baselineSpacing = 3; // or whatever vertical spacing you want
    const baselineOffset = baselineSpacing * baselineCount;
    const hasMilestoneLabels = false;
    const betweenBaselineMilestoneGap =
      baselineCount > 1 && hasMilestoneLabels ? 24 : 0;
    const pushRecalcBelowActualMain =
      baselineCount === 1 &&
        hasMilestoneLabels &&
        task.id.endsWith("-act")
        ? 24
        : 0;

    const adjustedHeight =
      task.typeInternal !== "milestone"
        ? props.taskHeight -
        baselineOffset -
        10 * (baselineCount > 1 ? 0.99 : 1)
        : props.taskHeight;

    taskHeight = adjustedHeight;

    // Updated props for main task
    const updatedProps = {
      ...props,
      onMilestoneMouseEnter: handleMilestoneMouseEnter,
      onMilestoneMouseLeave: handleMilestoneMouseLeave,
      task: {
        ...props.task,
        height:
          task.typeInternal !== "milestone"
            ? adjustedHeight
            : hasBaseline
              ? props.task.height - 5
              : props.task.height,
        ...(task.typeInternal !== "milestone"
          ? {
            y: hasBaseline ? task.y - baselineOffset : task.y,
          }
          : {}),
      },
    };
    const finalProps = hasBaseline
      ? updatedProps
      : {
        ...props,
        onMilestoneMouseEnter: handleMilestoneMouseEnter,
        onMilestoneMouseLeave: handleMilestoneMouseLeave,
      };
    // Render primary task
    switch (task.typeInternal) {
      case "milestone":
        setTaskItem(<Milestone {...finalProps} />);
        break;
      case "project":
        setTaskItem(<Project {...finalProps} />);
        break;
      case "smalltask":
        setTaskItem(<Bar {...finalProps} />);
        break;
      default:
        setTaskItem(<Bar {...finalProps} />);
        break;
    }

    // Render baseline bars
    if (hasBaseline) {
      const baselineElements = (task.baseLineTask as BarTask[]).map(
        (blTask, index) => {
          const baselineProps = {
            ...props,
            task: {
              ...blTask,
              height: adjustedHeight,
              y:
                task.y +
                props.taskHeight +
                index *
                (baselineSpacing * baselineCount * 2.5 +
                  betweenBaselineMilestoneGap) -
                10 * baselineCount +
                pushRecalcBelowActualMain,

              assign: [],
            },
          };
          return (
            <g
              key={`${blTask.id ?? "baseline"}-${index}`}
              onMouseEnter={(e) => handleMouseEnterBaseLine(e, blTask)}
              onMouseLeave={(e) => handleMouseLeaveBaseLine(e, blTask)}
              data-taskid={blTask.id}
            >
              {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-expect-error
                <TaskItem {...baselineProps} />
              }
            </g>
          );
        },
      );

      setBaselineItem(
        <g onMouseLeave={(e) => handleMouseLeaveBaseLine(e, task)}>
          {baselineElements}
        </g>,
      );
    } else {
      setBaselineItem(null);
    }

    // Set user assignment icon container width
    setUserWidth(
      task.assign && task.assign?.length > 0 ? task.assign.length * 32 + 10 : 0,
    );
  }, [
    task,
    isSelected,
    handleMilestoneMouseEnter,
    handleMilestoneMouseLeave,
    props.taskHeight,
  ]);

  useEffect(() => {
    if (textRef.current) {
      setIsTextInside(false);
    }
  }, [textRef, task]);

  const isMilestonePointerTarget = (target: EventTarget | null) =>
    Boolean(
      target instanceof Element &&
      target.closest?.("[data-milestone-hit='true']"),
    );

  const getX = () => {
    const width = task.x2 - task.x1;
    const hasChild = task.barChildren.length > 0;
    if (isTextInside) {
      return task.x1 + width * 0.5;
    }
    if (rtl && textRef.current) {
      return (
        task.x1 -
        textRef.current.getBBox().width -
        arrowIndent * +hasChild -
        arrowIndent * 0.2
      );
    } else {
      return task.x1 + width + arrowIndent * +hasChild + arrowIndent * 0.2;
    }
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (isMilestonePointerTarget(e.target)) {
      return;
    }
    setTooltip({
      visible: true,
      x: e.clientX + 10,
      y: e.clientY + 10,
      content: `Task: ${task.name}`,
    });
    onEventStart("mouseenter", task, e);
  };

  const handleMouseEnterBaseLine = (
    e: React.MouseEvent,
    baseLineTask: BarTask,
  ) => {
    if (isMilestonePointerTarget(e.target)) {
      return;
    }
    setTooltip({
      visible: true,
      x: e.clientX + 10,
      y: e.clientY + 10,
      content: `Task: ${baseLineTask.name}`,
    });
    onEventStart("mouseenter", baseLineTask, e);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (tooltip.visible) {
      setTooltip({
        ...tooltip,
        x: e.clientX + 10,
        y: e.clientY + 10,
      });
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    setTooltip({
      ...tooltip,
      visible: false,
    });
    onEventStart("mouseleave", task, e);
  };
  const handleMouseLeaveBaseLine = (
    e: React.MouseEvent,
    baseLineTask: BarTask,
  ) => {
    setTooltip({
      ...tooltip,
      visible: false,
    });
    if (ganttEvent.action !== "contextmenu") {
      setGanttEvent({ action: "" });
    }
    onEventStart("mouseleave", baseLineTask, e);
  };
  const taskref = useRef<SVGAElement | null>(null);
  useEffect(() => {
    if (selectedTasks && taskref.current && task.id === selectedTasks.id) {
      const element = taskref.current;
      const container = element.closest("svg")?.parentElement;
      if (container) {
        setTimeout(() => {
          onselectionchange();
        }, 1000);
      }
    }
  }, [selectedTasks]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const parentTarget = target.parentNode?.parentNode as HTMLElement;
      if (parentTarget?.getAttribute("data-userid")) {
        setGanttEvent({
          action: "mouseenter",
          changedTask: { ...task, showAll: false } as any,
          originalSelectedTask: task,
          optional_data: {
            userId: parentTarget?.getAttribute("data-userid"),
            showAll: parentTarget?.getAttribute("data-showallassing"),
            efforts: parentTarget?.getAttribute("data-efforts"),
          },
        });
      } else if (target?.getAttribute("data-userid")) {
        setGanttEvent({
          action: "mouseenter",
          changedTask: { ...task, showAll: true } as any,
          originalSelectedTask: task,
          optional_data: {
            userId: target?.getAttribute("data-userid"),
            showAll: target?.getAttribute("data-showallassing"),
            efforts: target?.getAttribute("data-efforts"),
          },
        });
      }
    };

    taskref.current?.addEventListener("mousemove", handleMouseMove);
    return () => {
      taskref.current?.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const location = useLocation();

  return (
    <>
      <g
        ref={taskref}
        onKeyDown={(e) => {
          switch (e.key) {
            case "Delete": {
              if (isDelete) onEventStart("delete", task, e);
              break;
            }
          }
          e.stopPropagation();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (task.isDisabled) {
            return;
          }
          if (ganttEvent.action == "contextmenu") {
            setGanttEvent({
              action: "",
              changedTask: task,
              originalSelectedTask: task,
            });
          }
          if (
            e.currentTarget &&
            e.button == 2 &&
            ganttEvent.action !== "contextmenu"
          ) {
            setGanttEvent({
              action: "contextmenu",
              changedTask: task,
              originalSelectedTask: task,
            });
            taskref.current?.focus();
          }
        }}
        onBlur={() => {
          if (ganttEvent.action !== "contextmenu") {
            onEventStart("", task);
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onDoubleClick={(e) => {
          onEventStart("dblclick", task, e);
        }}
        onClick={(e) => {
          onEventStart("click", task, e);
        }}
        onFocus={() => {
          onEventStart("select", task);
        }}
        className={`group ${selectedTasks && selectedTasks.id === task.id
          ? "opacity-100 contrast-100"
          : "opacity-90 "
          } ${selectedTasks === undefined ? " !opacity-100 contrast-100" : " "} ${pop.includes(task.id) ? " !opacity-100 contrast-100" : ""
          } duration-50000 transition-opacity select-none`}
      >
        {canCreateDependency && (
          <svg
            fill={allow ? task.styles.backgroundSelectedColor : "#ff000044"}
            id={task.id}
            onMouseUp={(e) =>
              props.endDrag(e, { x: task.x1 - 8, y: task.y + 15 })
            }
            onMouseDown={(e) =>
              props.startDrag(e, { x: task.x1 - 8, y: task.y + 15 })
            }
            className="opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
            x={task.x1 - 28}
            y={task.y - 10}
            z={1000}
            width="50px"
            height="50px"
            viewBox="0 0 20 20"
            data-type={"start"}
            data-taskid={task.id}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              data-type={"start"}
              data-taskid={task.id}
              d="M7.8 10a2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0-4.4 0z"
            />
          </svg>
        )}
        {taskItem}

        {canCreateDependency && (
          <svg
            id={task.id}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            fill={allow ? task.styles.backgroundSelectedColor : "#ff000044"}
            onMouseUp={(e) =>
              props.endDrag(e, { x: task.x2 + 8, y: task.y + 15 })
            }
            onMouseDown={(e) =>
              props.startDrag(e, { x: task.x2 + 8, y: task.y + 15 })
            }
            x={task.x2 - 22}
            y={task.y - 10}
            width="50px"
            height="50px"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            data-type={"end"}
            data-taskid={task.id}
          >
            <path
              data-type={"end"}
              data-taskid={task.id}
              d="M7.8 10a2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0-4.4 0z"
            />
          </svg>
        )}
        {!baselineItem && (
          <foreignObject
            x={getX() + 10}
            y={
              task.typeInternal === "project"
                ? task.y + taskHeight * 0.34 - 9
                : task.y + taskHeight * 0.5 - 17
            }
            style={{ width: userWidth }}
            className={
              isTextInside
                ? style.barLabel
                : style.barLabel &&
                style.barLabelOutside +
                " select-none h-9 " +
                style.foreignObject
            }
          >
            <div className="flex w-18">
              <div className="flex w-18 h-9 items-end">
                {task.assign?.slice(0, 5).map((item, index) => {
                  const efforts = calculateTaskEffort(
                    item?.workingHours ?? 8,
                    task.duration ?? 1,
                    8,
                  );
                  return (
                    <div
                      key={item.user?.userId ?? `assign-${index}`}
                      data-userid={item.user.userId}
                      data-efforts={efforts}
                      className={index > 0 ? "" : ""}
                    >
                      <UserAvatar
                        className="shadow-sm shadow-gray-300 border-2 border-white scale-100"
                        user={item.user}
                      />
                    </div>
                  );
                })}
                {task.assign?.length > 5 &&
                  (location.pathname.includes("projects") ? (
                    <Link
                      to={"/members/" + task.id}
                      className="ml-1 text-xs text-gray-600 font-medium"
                    >
                      +{task.assign?.length - 5}
                    </Link>
                  ) : (
                    <div
                      className="ml-1 w-8 h-8 aspect-square justify-center items-center flex  rounded-full bg-slate-200 outline-none"
                      data-userid={"all"}
                      data-showallassing="true"
                    >
                      +{task.assign?.length - 5}
                    </div>
                  ))}
              </div>
            </div>
          </foreignObject>
        )}
      </g>

      {baselineItem}
    </>
  );
};
